/**
 * ApexProvider — global state for the floating Apex assistant.
 *
 * Owns:
 *  - chat list + currently-selected chatId (persisted in localStorage)
 *  - in-memory messages for the current chat
 *  - drawer + history-rail open state
 *  - PageContext (slice 8): merged route, openEntity, filters, recentEdits
 *  - SSE plumbing for sendMessage() (delegates to lib/api.ts:streamAi)
 *  - Frontend tool dispatch: navigate_to + open_entity (slice 5b)
 *
 * Mounted INSIDE BrowserRouter so it can use useNavigate / useLocation.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import type { UploadFile } from "antd";
import { useLocation, useNavigate } from "react-router-dom";

import {
  chatsApi,
  queryAuditLog,
  streamAi,
  type ChatSummary,
} from "../../lib/api";
import { useAuth } from "../../lib/auth";
import {
  routeName as deriveRouteName,
  type PageContext,
  type RecentEdit,
} from "../../lib/apexContext";
import { isApexHiddenPath } from "./visibility";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface UploadedFile {
  uid: string;
  file: File;
  content: string;
  type: string;
}

export interface ApexConfirmation {
  /** Backend tool name (kept opaque to the user; never shown raw). */
  tool: string;
  /** Backend args (passed back verbatim on Confirm; never shown raw). */
  args: Record<string, unknown>;
  /** Plain-language summary the model wrote — shown bold above the buttons. */
  summary: string;
  /** Optional reasoning shown inside a collapsible "Why?" panel. */
  reasoning?: string;
  /** Optional sessionId echoed by backend to thread through to confirm step. */
  sessionId?: string;
}

export interface ApexMessage {
  role: "assistant" | "user";
  text: string;
  thoughts?: string;
  attachments?: UploadFile[];
  streaming?: boolean;
  /** When set, the message renders an inline approval card instead of plain text. */
  confirmation?: ApexConfirmation;
}

export interface SendMessageOptions {
  files?: UploadedFile[];
  /** When set, the call posts back a previously-asked-for confirmation. */
  confirmedTool?: { name: string; args: Record<string, unknown> };
}

interface ApexContextValue {
  // chat state
  chats: ChatSummary[];
  currentChatId: string;
  messages: ApexMessage[];
  loadingChats: boolean;
  loadingMessages: boolean;
  sending: boolean;

  // drawer + UI
  drawerOpen: boolean;
  historyOpen: boolean;
  /** Width (px) of the Apex side panel. Persisted across sessions. */
  apexWidth: number;
  /** Update the Apex panel width (clamped 320–1024). */
  setApexWidth: (w: number) => void;
  /**
   * When true, the layout pushes its right margin equal to apexWidth so the
   * panel sits beside content. False on narrow viewports — the panel overlays
   * instead of pushing.
   */
  pushMode: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleHistory: () => void;

  // chat lifecycle
  newChat: () => void;
  loadChat: (chatId: string) => Promise<void>;
  deleteChat: (chatId: string) => Promise<void>;
  renameChat: (chatId: string, title: string) => Promise<void>;
  refreshChats: () => Promise<void>;

  // page context
  pageContext: PageContext;
  setPageContext: (patch: Partial<PageContext>) => void;

  // chat send
  sendMessage: (prompt: string, options?: SendMessageOptions) => Promise<void>;
  cancelConfirmation: (confirmation: ApexConfirmation) => Promise<void>;
}

const ApexContext = createContext<ApexContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STORAGE_CHAT_ID = "apex.currentChatId";
const STORAGE_DRAWER = "apex.drawerOpen";
const STORAGE_WIDTH = "apex.width";

export const APEX_WIDTH_DEFAULT = 420;
export const APEX_WIDTH_MIN = 320;
export const APEX_WIDTH_MAX = 1024;
/**
 * matchMedia query that flips us into overlay mode (pushMode=false) when the
 * viewport is too narrow to comfortably push content aside. Above this breakpoint
 * the panel sits beside the page; below it, the panel overlays.
 */
export const APEX_OVERLAY_MEDIA_QUERY = "(max-width: 1024px)";

function clampWidth(w: number): number {
  if (Number.isNaN(w)) return APEX_WIDTH_DEFAULT;
  if (w < APEX_WIDTH_MIN) return APEX_WIDTH_MIN;
  if (w > APEX_WIDTH_MAX) return APEX_WIDTH_MAX;
  return Math.round(w);
}

function newChatId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `chat_${Math.random().toString(36).slice(2)}_${Date.now()}`;
}

function readLocal(key: string): string | null {
  try {
    return typeof window !== "undefined" ? window.localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string | null): void {
  try {
    if (typeof window === "undefined") return;
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch {
    /* swallow quota / private-mode errors */
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function ApexProvider({ children }: { children: ReactNode }) {
  const { user, allowlisted } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Stable navigate ref so SSE handlers (closures) can fire frontend tools.
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);

  // Always start each mount with a fresh chatId. We deliberately ignore the
  // previously-saved STORAGE_CHAT_ID on boot so a hard reload always lands on
  // the welcome screen — old chats stay reachable through the history rail
  // (OAIP-97). The localStorage write below keeps the persisted value in sync
  // with `currentChatId` for sub-trees that read it directly.
  const [currentChatId, setCurrentChatId] = useState<string>(() => {
    const fresh = newChatId();
    writeLocal(STORAGE_CHAT_ID, fresh);
    return fresh;
  });

  const [messages, setMessages] = useState<ApexMessage[]>([]);

  // Initial drawer state: persisted preference, but suppressed on hidden routes
  // so a drawer left open on Dashboard doesn't appear over /agency or /vendor.
  const [drawerOpen, setDrawerOpen] = useState<boolean>(() => {
    if (isApexHiddenPath(location.pathname)) return false;
    return readLocal(STORAGE_DRAWER) === "1";
  });
  const [historyOpen, setHistoryOpen] = useState<boolean>(false);

  // Resizable side-panel width (persisted across sessions).
  const [apexWidth, setApexWidthState] = useState<number>(() => {
    const saved = readLocal(STORAGE_WIDTH);
    if (!saved) return APEX_WIDTH_DEFAULT;
    const parsed = Number.parseInt(saved, 10);
    if (Number.isNaN(parsed)) return APEX_WIDTH_DEFAULT;
    return clampWidth(parsed);
  });

  const setApexWidth = useCallback((w: number) => {
    const clamped = clampWidth(w);
    setApexWidthState(clamped);
    writeLocal(STORAGE_WIDTH, String(clamped));
  }, []);

  // pushMode: true when viewport is wide enough to host a side-by-side layout.
  // We listen for changes so the layout reacts to live resizes.
  const [pushMode, setPushMode] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return true;
    }
    return !window.matchMedia(APEX_OVERLAY_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mq = window.matchMedia(APEX_OVERLAY_MEDIA_QUERY);
    const handler = (e: MediaQueryListEvent) => {
      setPushMode(!e.matches);
    };
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
    // Legacy Safari fallback.
    if (typeof mq.addListener === "function") {
      mq.addListener(handler);
      return () => mq.removeListener(handler);
    }
    return undefined;
  }, []);

  const [pageContext, setPageContextState] = useState<PageContext>(() => ({
    route: location.pathname,
    routeName: deriveRouteName(location.pathname),
  }));

  const setPageContext = useCallback((patch: Partial<PageContext>) => {
    setPageContextState((prev) => ({ ...prev, ...patch }));
  }, []);

  // Keep localStorage in sync for chatId + drawer state.
  useEffect(() => {
    writeLocal(STORAGE_CHAT_ID, currentChatId);
  }, [currentChatId]);
  useEffect(() => {
    // Don't persist drawer state while on a hidden route — the auto-close
    // there is forced, not a user choice. Persisting it would clobber the
    // user's real preference next time they return to an internal page.
    if (isApexHiddenPath(location.pathname)) return;
    writeLocal(STORAGE_DRAWER, drawerOpen ? "1" : "0");
  }, [drawerOpen, location.pathname]);

  // Update route + routeName whenever the location changes.
  useEffect(() => {
    setPageContextState((prev) => ({
      ...prev,
      route: location.pathname,
      routeName: deriveRouteName(location.pathname),
    }));
  }, [location.pathname]);

  // Auto-close the drawer when entering a hidden route. We deliberately do not
  // touch localStorage here so navigating back to an internal page restores the
  // user's prior preference.
  useEffect(() => {
    if (isApexHiddenPath(location.pathname)) {
      setDrawerOpen(false);
    }
  }, [location.pathname]);

  // ---------------- Chat list bootstrapping ----------------

  const refreshChats = useCallback(async () => {
    if (!allowlisted) return;
    setLoadingChats(true);
    try {
      const result = await chatsApi.list();
      setChats(result.chats || []);
    } catch (err) {
      // Backend handler may not exist yet during slice rollout — fail soft.
      console.warn("[apex] chats.list failed:", err);
      setChats([]);
    } finally {
      setLoadingChats(false);
    }
  }, [allowlisted]);

  // Initial bootstrap when the user becomes allowlisted.
  useEffect(() => {
    if (!allowlisted) {
      setChats([]);
      setMessages([]);
      return;
    }
    void refreshChats();
  }, [allowlisted, refreshChats]);

  // Intentional: no on-mount auto-load of the previous chat. Welcome screen
  // shows on every hard reload (OAIP-97); users resume an old chat by clicking
  // it in the history rail, which routes through `loadChat` below.

  // ---------------- Page-context: recentEdits when drawer opens ----------------

  useEffect(() => {
    if (!drawerOpen || !allowlisted || !user?.email) return;
    let cancelled = false;
    void (async () => {
      try {
        const result = await queryAuditLog({ actors: [user.email!.toLowerCase()], limit: 5 });
        if (cancelled) return;
        const edits: RecentEdit[] = (result.entries || []).map((e) => ({
          action: e.action,
          collection: e.collection,
          docId: e.docId,
          ts: e.ts,
        }));
        setPageContextState((prev) => ({ ...prev, recentEdits: edits }));
      } catch {
        /* best-effort — context is enrichment, not required */
      }
    })();
    return () => {
      cancelled = true;
    };
    // TODO: wire unreadNotificationCount once notification listener is hoisted to ApexProvider.
  }, [drawerOpen, allowlisted, user?.email]);

  // ---------------- Drawer + history actions ----------------

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const toggleHistory = useCallback(() => setHistoryOpen((prev) => !prev), []);

  // ---------------- Chat lifecycle ----------------

  const newChat = useCallback(() => {
    const fresh = newChatId();
    setCurrentChatId(fresh);
    setMessages([]);
  }, []);

  const loadChat = useCallback(async (chatId: string) => {
    setLoadingMessages(true);
    setCurrentChatId(chatId);
    try {
      const detail = await chatsApi.get(chatId);
      const restored: ApexMessage[] = (detail?.messages ?? []).map((m) => ({
        role: m.role,
        text: m.text,
        thoughts: m.thoughts,
      }));

      // If the chat was suspended mid-tool-confirmation, re-emit the approval
      // card so the user can resume from the rail. The doc only stores
      // {tool, args, timestamp} (ai.py:848-852) — `summary` and `reasoning`
      // were SSE-only meta and are not persisted, so we use a generic summary.
      const pc = (detail as { pendingConfirmation?: { tool?: string; args?: Record<string, unknown> } } | null)
        ?.pendingConfirmation;
      if (pc?.tool) {
        restored.push({
          role: "assistant",
          text: "",
          confirmation: {
            tool: pc.tool,
            args: pc.args || {},
            summary: "Confirm this action?",
            sessionId: chatId,
          },
        });
      }

      setMessages(restored);
    } catch (err) {
      console.warn("[apex] failed to load chat:", err);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  const deleteChat = useCallback(
    async (chatId: string) => {
      try {
        await chatsApi.delete(chatId);
      } catch (err) {
        console.warn("[apex] failed to delete chat:", err);
      }
      await refreshChats();
      if (chatId === currentChatId) {
        const fresh = newChatId();
        setCurrentChatId(fresh);
        setMessages([]);
      }
    },
    [currentChatId, refreshChats]
  );

  const renameChat = useCallback(
    async (chatId: string, title: string) => {
      try {
        await chatsApi.rename(chatId, title);
      } catch (err) {
        console.warn("[apex] failed to rename chat:", err);
      }
      await refreshChats();
    },
    [refreshChats]
  );

  // ---------------- Frontend tool dispatch ----------------

  const handleFrontendTool = useCallback((meta: { name?: string; args?: Record<string, unknown> }) => {
    const name = meta?.name;
    const args = (meta?.args ?? {}) as Record<string, unknown>;
    if (!name) return;
    if (name === "navigate_to" && typeof args.route === "string") {
      navigateRef.current(args.route);
      return;
    }
    if (name === "open_entity") {
      window.dispatchEvent(new CustomEvent("apex:openEntity", { detail: args }));
      return;
    }
  }, []);

  // ---------------- Send message ----------------

  const sendMessage = useCallback(
    async (prompt: string, options?: SendMessageOptions) => {
      const trimmed = prompt.trim();
      const hasText = trimmed.length > 0;
      const hasFiles = (options?.files?.length ?? 0) > 0;
      const isConfirmation = !!options?.confirmedTool;
      if (!hasText && !hasFiles && !isConfirmation) return;
      if (sending) return;

      // Append user-visible user message (skip when this is just a tool-confirm step).
      const newUser: ApexMessage | null = !isConfirmation
        ? {
            role: "user",
            text: hasText ? prompt : "Please review the attached file(s).",
            attachments: undefined,
          }
        : null;

      const historyForSend: ApexMessage[] = newUser ? [...messages, newUser] : [...messages];
      if (newUser) {
        setMessages((prev) => [...prev, newUser]);
      }

      setSending(true);
      let assistantIndex = -1;
      let hasContent = false;

      const upsertAssistantText = (chunk: string) => {
        if (!hasContent) {
          setMessages((prev) => {
            assistantIndex = prev.length;
            return [...prev, { role: "assistant", text: chunk, streaming: true }];
          });
          hasContent = true;
        } else {
          setMessages((prev) =>
            prev.map((m, idx) =>
              idx === assistantIndex ? { ...m, text: `${m.text}${chunk}`, streaming: true } : m
            )
          );
        }
      };

      const upsertAssistantThought = (chunk: string) => {
        if (!hasContent) {
          setMessages((prev) => {
            assistantIndex = prev.length;
            return [
              ...prev,
              { role: "assistant", text: "", thoughts: chunk, streaming: true },
            ];
          });
          hasContent = true;
        } else {
          setMessages((prev) =>
            prev.map((m, idx) =>
              idx === assistantIndex
                ? { ...m, thoughts: `${m.thoughts ?? ""}${chunk}`, streaming: true }
                : m
            )
          );
        }
      };

      const attachments: Array<{ mimeType: string; data: string }> = [];
      for (const f of options?.files ?? []) {
        if (f.content) attachments.push({ mimeType: f.type, data: f.content });
      }

      try {
        await streamAi(
          isConfirmation ? "" : (newUser?.text ?? ""),
          undefined,
          upsertAssistantText,
          {
            sessionId: currentChatId,
            history: historyForSend.map((m) => ({ role: m.role, text: m.text })),
            toolsEnabled: true,
            attachments,
            requireConfirmation: true,
            confirmedTool: options?.confirmedTool,
            pageContext: pageContext as unknown as Record<string, unknown>,
            onThought: upsertAssistantThought,
            onMeta: (meta: any) => {
              if (!meta || typeof meta !== "object") return;

              // Frontend tool meta event.
              if (meta.type === "frontend_tool") {
                handleFrontendTool(meta);
                return;
              }

              // Approval card: backend now sends `summary` + `reasoning`.
              if (meta.confirmation_required || meta.summary) {
                const cr = meta.confirmation_required;
                const confirmation: ApexConfirmation = {
                  tool: meta.tool || cr?.tool || "unknown",
                  args:
                    (meta.args as Record<string, unknown>) ||
                    cr?.args ||
                    {},
                  // Prefer the registry's plain-English summary over the
                  // generic "Execute X with the following parameters?" fallback.
                  summary:
                    cr?.summary ||
                    meta.summary ||
                    cr?.message ||
                    "Confirm this action?",
                  reasoning: meta.reasoning || cr?.reasoning,
                  sessionId:
                    meta.sessionId ||
                    cr?.sessionId ||
                    currentChatId,
                };
                setMessages((prev) => [
                  ...prev,
                  { role: "assistant", text: "", confirmation },
                ]);
              }
            },
            onConfirmationRequired: (confirmation: any) => {
              const card: ApexConfirmation = {
                tool: confirmation.tool || "unknown",
                args: (confirmation.args as Record<string, unknown>) || {},
                summary:
                  confirmation.summary || confirmation.message || "Confirm this action?",
                reasoning: confirmation.reasoning,
                sessionId: confirmation.sessionId || currentChatId,
              };
              setMessages((prev) => [
                ...prev,
                { role: "assistant", text: "", confirmation: card },
              ]);
            },
          }
        );

        // Mark final assistant text as not-streaming.
        setMessages((prev) =>
          prev.map((m, idx) => (idx === assistantIndex ? { ...m, streaming: false } : m))
        );
      } catch (err) {
        console.error("[apex] streamAi failed:", err);
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            text: "Sorry — I hit an error reaching the model. Try again in a moment.",
          },
        ]);
      } finally {
        setSending(false);
        // Refresh chat list so the new turn shows up in the rail.
        void refreshChats();
      }
    },
    [
      sending,
      messages,
      currentChatId,
      pageContext,
      handleFrontendTool,
      refreshChats,
    ]
  );

  // ---------------- Cancel an in-flight confirmation ----------------

  const cancelConfirmation = useCallback(
    async (_confirmation: ApexConfirmation) => {
      // Remove the confirmation card from the rendered messages immediately.
      setMessages((prev) => prev.filter((m) => !m.confirmation));
      try {
        // Best-effort cancel POST — the backend (slice 9) runs a final ack turn.
        await streamAi("", undefined, undefined, {
          sessionId: currentChatId,
          cancelled: true,
          pageContext: pageContext as unknown as Record<string, unknown>,
          history: messages.map((m) => ({ role: m.role, text: m.text })),
        });
      } catch (err) {
        // Cancel is fire-and-forget; log but don't surface.
        console.warn("[apex] cancel POST failed:", err);
      }
      void refreshChats();
    },
    [currentChatId, messages, pageContext, refreshChats]
  );

  const value = useMemo<ApexContextValue>(
    () => ({
      chats,
      currentChatId,
      messages,
      loadingChats,
      loadingMessages,
      sending,
      drawerOpen,
      historyOpen,
      apexWidth,
      setApexWidth,
      pushMode,
      openDrawer,
      closeDrawer,
      toggleHistory,
      newChat,
      loadChat,
      deleteChat,
      renameChat,
      refreshChats,
      pageContext,
      setPageContext,
      sendMessage,
      cancelConfirmation,
    }),
    [
      chats,
      currentChatId,
      messages,
      loadingChats,
      loadingMessages,
      sending,
      drawerOpen,
      historyOpen,
      apexWidth,
      setApexWidth,
      pushMode,
      openDrawer,
      closeDrawer,
      toggleHistory,
      newChat,
      loadChat,
      deleteChat,
      renameChat,
      refreshChats,
      pageContext,
      setPageContext,
      sendMessage,
      cancelConfirmation,
    ]
  );

  return <ApexContext.Provider value={value}>{children}</ApexContext.Provider>;
}

export function useApex(): ApexContextValue {
  const ctx = useContext(ApexContext);
  if (!ctx) throw new Error("useApex must be used inside <ApexProvider>");
  return ctx;
}

/**
 * Page-level convenience: returns the live PageContext + setter.
 *
 * Designed to be safe outside an `<ApexProvider>` (e.g. when a page is
 * mounted in isolation by a test harness or storybook). In that case the
 * setter is a no-op and the context is a stub — pages keep working but
 * Apex doesn't see any of their context updates.
 */
export function useApexContext() {
  const ctx = useContext(ApexContext);
  if (!ctx) {
    return {
      pageContext: { route: "/", routeName: "" } as PageContext,
      setPageContext: (_patch: Partial<PageContext>) => undefined,
    };
  }
  return { pageContext: ctx.pageContext, setPageContext: ctx.setPageContext };
}

/**
 * Modal-friendly read-only view of the layout-relevant Apex state.
 *
 * Safe to call without `<ApexProvider>` in scope — returns sensible defaults
 * (drawer closed, default width, push enabled). Lets dialog/modal components
 * shift their mask/position out of Apex's region without forcing every test
 * harness to re-mount the provider.
 */
export function useApexLayout(): {
  drawerOpen: boolean;
  apexWidth: number;
  pushMode: boolean;
} {
  const ctx = useContext(ApexContext);
  if (!ctx) {
    return { drawerOpen: false, apexWidth: APEX_WIDTH_DEFAULT, pushMode: true };
  }
  return {
    drawerOpen: ctx.drawerOpen,
    apexWidth: ctx.apexWidth,
    pushMode: ctx.pushMode,
  };
}
